<!-- Aba "Convites" da tela de Contas Membro.

     Conta que já existia entra no círculo por convite: aparece aqui até a
     pessoa responder e, se aceitar, passa a aparecer também na aba Membros.
     Pendente vencido (7 dias) chega da API já como expirado.

     Reenviar vale para pendente (lembra por e-mail e renova o prazo) e para
     expirado (reabre). Cancelar só para pendente; o registro fica, para o
     histórico. Sem modal de confirmação: os dois são reversíveis (dá para
     convidar de novo). -->
<script setup>
import { onMounted, ref } from "vue";
import * as managedAccountService from "@/services/managedAccountService";
import { asUtc, formatDate } from "@/utils/dateUtils";

const emit = defineEmits(["notify", "count"]);

const invitations = ref([]);
const isLoading = ref(true);
const loadError = ref("");
// Ação em andamento por convite, para travar os botões da linha.
const busy = ref({});

const STATUS = {
  pending: { label: "Aguardando", cls: "status-chip--pending" },
  accepted: { label: "Aceito", cls: "status-chip--active" },
  declined: { label: "Recusado", cls: "status-chip--inactive" },
  cancelled: { label: "Cancelado", cls: "status-chip--neutral" },
  expired: { label: "Expirado", cls: "status-chip--neutral" },
};

function statusOf(inv) {
  return STATUS[inv.status] ?? { label: inv.status, cls: "status-chip--neutral" };
}

function statusDetail(inv) {
  if (inv.status === "pending") return `vale até ${formatDate(asUtc(inv.expiresAt))}`;
  if (inv.status === "cancelled" && inv.cancelledByName)
    return `por ${inv.cancelledByName}`;
  if (inv.respondedAt) return `em ${formatDate(asUtc(inv.respondedAt))}`;
  return "";
}

async function load() {
  isLoading.value = true;
  loadError.value = "";
  try {
    const { data } = await managedAccountService.fetchInvitations();
    invitations.value = data;
    emit("count", data.filter((i) => i.status === "pending").length);
  } catch (err) {
    loadError.value =
      err.response?.data?.message || "Não foi possível carregar os convites.";
  } finally {
    isLoading.value = false;
  }
}

function replace(updated) {
  invitations.value = invitations.value.map((i) =>
    i.invitationId === updated.invitationId ? updated : i
  );
  emit("count", invitations.value.filter((i) => i.status === "pending").length);
}

async function run(inv, action, successMessage) {
  busy.value = { ...busy.value, [inv.invitationId]: true };
  try {
    const { data } = await action(inv.invitationId);
    replace(data);
    emit("notify", { type: "success", message: successMessage });
  } catch (err) {
    emit("notify", {
      type: "error",
      message: err.response?.data?.message || "Não foi possível concluir a ação.",
    });
    load();
  } finally {
    busy.value = { ...busy.value, [inv.invitationId]: false };
  }
}

function resend(inv) {
  run(
    inv,
    managedAccountService.resendInvitation,
    inv.status === "expired"
      ? "Convite reaberto. A pessoa tem mais 7 dias para responder."
      : "Convite reenviado. O prazo foi renovado por mais 7 dias."
  );
}

function cancel(inv) {
  run(inv, managedAccountService.cancelInvitation, "Convite cancelado.");
}

onMounted(load);

defineExpose({ load });
</script>

<template>
  <div class="table-wrapper">
    <table class="main-table">
      <thead>
        <tr>
          <th style="width: 26%">Pessoa</th>
          <th style="width: 13%">Perfil</th>
          <th style="width: 12%">Turma</th>
          <th style="width: 15%">Enviado por</th>
          <th style="width: 11%">Enviado em</th>
          <th style="width: 13%">Situação</th>
          <th style="width: 10%">Ações</th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="isLoading" class="empty-row-state">
          <td colspan="7" class="empty-row empty-row-state">
            <div class="empty-state-content">
              <p>Carregando convites...</p>
            </div>
          </td>
        </tr>

        <tr v-else-if="loadError" class="empty-row-state">
          <td colspan="7" class="empty-row empty-row-state">
            <div class="empty-state-content">
              <p>{{ loadError }}</p>
              <button type="button" class="btn-link" @click="load">
                Tentar de novo
              </button>
            </div>
          </td>
        </tr>

        <tr v-else-if="invitations.length === 0" class="empty-row-state">
          <td colspan="7" class="empty-row empty-row-state">
            <div class="empty-state-content">
              <img src="/img/lupa.webp" alt="" width="38" height="40" class="empty-icon-img" />
              <p>Nenhum convite enviado.</p>
              <p class="invites-empty-hint">
                Quem já tem conta no Litterae entra no seu círculo por convite.
                Use "Adicionar Conta Membro" e a aba "Convidar existente".
              </p>
            </div>
          </td>
        </tr>

        <tr v-for="inv in invitations" v-else :key="inv.invitationId">
          <td class="cell-text-left">
            <div class="invite-person">
              <span class="invite-person-name">{{
                inv.invitedFullName || inv.invitedUserName
              }}</span>
              <span class="invite-person-meta"
                >@{{ inv.invitedUserName }} · {{ inv.invitedEmail }}</span
              >
            </div>
          </td>
          <td>
            <span>{{ inv.relationshipType }}</span>
          </td>
          <td>
            <span>{{ inv.groupName || "-" }}</span>
          </td>
          <td>
            <span>{{ inv.invitedByName || "-" }}</span>
          </td>
          <td>
            <span>{{ formatDate(asUtc(inv.createdAt)) }}</span>
          </td>
          <td>
            <div class="invite-status">
              <span class="status-chip" :class="statusOf(inv).cls">{{
                statusOf(inv).label
              }}</span>
              <span v-if="statusDetail(inv)" class="invite-status-detail">{{
                statusDetail(inv)
              }}</span>
            </div>
          </td>
          <td>
            <div class="row-actions">
              <button
                v-if="inv.status === 'pending' || inv.status === 'expired'"
                type="button"
                class="btn-icon"
                :disabled="busy[inv.invitationId]"
                :title="inv.status === 'expired' ? 'Reabrir o convite' : 'Reenviar o e-mail e renovar o prazo'"
                :aria-label="inv.status === 'expired' ? 'Reabrir o convite' : 'Reenviar o convite'"
                @click="resend(inv)"
              >
                <i class="fas fa-paper-plane" aria-hidden="true"></i>
              </button>
              <button
                v-if="inv.status === 'pending'"
                type="button"
                class="btn-icon"
                :disabled="busy[inv.invitationId]"
                title="Cancelar o convite"
                aria-label="Cancelar o convite"
                @click="cancel(inv)"
              >
                <i class="fas fa-ban" aria-hidden="true"></i>
              </button>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style scoped>
.invite-person {
  display: flex;
  flex-direction: column;
  max-width: 100%;
  margin: 0;
}
.invite-person-name {
  font-weight: var(--font-semibold);
  color: var(--text-dark);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.invite-person-meta {
  font-size: var(--text-xs);
  color: var(--text-light);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.invite-status {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  max-width: none;
}
.invite-status-detail {
  font-size: var(--text-xs);
  color: var(--text-light);
}
.invites-empty-hint {
  max-width: 28rem;
  font-size: var(--text-sm);
  color: var(--text-light);
  white-space: normal;
}
</style>
